import React, { PropsWithChildren, useContext, useEffect, useRef } from 'react'
import { ParallaxContext } from '../context/ParallaxContext'
import { useWindowDimensions } from '../hooks/useWindowDimensions'

export interface ParallaxProps {
  additionalClassName?: string
  id: string
  offset?: number
  scaleToFit?: boolean
}

export const Parallax = ({
  additionalClassName = '',
  children,
  id,
  offset,
  scaleToFit = true,
}: PropsWithChildren<ParallaxProps>) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const { documentHeight, screenHeight, screenWidth } = useWindowDimensions()
  const {
    addParallaxElement,
    removeParallaxElement,
    updateScaleFactor,
    updateYPosition,
  } = useContext(ParallaxContext)

  // Add the element to be parallaxed to the parallax context
  useEffect(() => {
    addParallaxElement({
      id,
      element: children,
      props: {
        additionalClassName: `-translate-z-1/2 scale-150 ${additionalClassName}`,
      },
    })

    return () => removeParallaxElement(id)
  }, [addParallaxElement, additionalClassName, children, id, removeParallaxElement])

  // Set the parallaxed elements y position by tracking the original element
  useEffect(() => {
    if (containerRef.current) {
      updateYPosition(id, offset ?? containerRef.current.offsetTop)
    }
  }, [children, documentHeight, screenWidth, id, offset, updateYPosition])

  // Apply an additional factor to the element if the viewport height is larger than the element height.
  // This to ensure that the element has sufficient height to cover the parallax area completely.
  // Note: The below calculation only works for a perspective factor of 1.5.
  useEffect(() => {
    if (scaleToFit && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      if (rect.height > 0 && screenHeight > rect.height) {
        const factor = -0.5 + screenHeight / (2 * rect.height)
        updateScaleFactor(id, factor)
      } else {
        updateScaleFactor(id, 0)
      }
    } else {
      updateScaleFactor(id, 0)
    }
  }, [
    children, // when children changes, the control remounts and scaling needs to be updated again
    documentHeight,
    id,
    scaleToFit,
    screenHeight,
    updateScaleFactor,
  ])

  // Hide the original element with opacity-0
  return (
    <div
      className={`opacity-0 pointer-events-none ${additionalClassName}`}
      ref={containerRef}
    >
      {children}
    </div>
  )
}
